import socketService from './socketService';
import httpChatService from './httpChatService';

class SmartChatService {
  constructor() {
    this.activeService = null;
    this.preferredService = 'socket'; // Try socket first
    this.fallbackService = 'http';
    this.connectionTimeout = 1000; // 1 second timeout for socket connection (reduced from 5s)
    this.listeners = new Map();
    this.isConnecting = false; // Prevent multiple simultaneous connections
  }

  // Initialize connection with fallback logic
  async connect(serverUrl) {
    // Prevent multiple simultaneous connections
    if (this.isConnecting) {
      console.log('SmartChatService: Connection already in progress, waiting...');
      return;
    }

    if (this.activeService) {
      console.log('SmartChatService: Already connected');
      return;
    }

    this.isConnecting = true;

    try {
      console.log('SmartChatService: Attempting to connect...');

      // Skip Socket.IO entirely in production serverless environments
      const isServerless = process.env.REACT_APP_BACKEND_URL?.includes('vercel.app') ||
        process.env.REACT_APP_BACKEND_URL?.includes('netlify.app') ||
        process.env.REACT_APP_BACKEND_URL?.includes('onrender.com') ||
        process.env.NODE_ENV === 'production';

      console.log('SmartChatService: Environment check:', {
        preferredService: this.preferredService,
        isServerless,
        backendUrl: process.env.REACT_APP_BACKEND_URL,
        nodeEnv: process.env.NODE_ENV,
        reactEnv: process.env.REACT_APP_ENV
      });

      if (this.preferredService === 'http' || isServerless) {
        console.log('SmartChatService: Using HTTP connection (serverless environment detected)');
        await httpChatService.connect();
        this.activeService = httpChatService;
        this.setupEventForwarding(httpChatService);
        console.log('SmartChatService: HTTP connection established');
        return;
      }

      // Try Socket.IO first (only in development or non-serverless environments)
      if (this.preferredService === 'socket') {
        try {
          console.log('SmartChatService: Trying Socket.IO connection...');

          // Set a shorter timeout for socket connection
          const socketPromise = socketService.connect(serverUrl);
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Socket connection timeout')), this.connectionTimeout);
          });

          await Promise.race([socketPromise, timeoutPromise]);

          // If we get here, socket connection succeeded
          this.activeService = socketService;
          this.setupEventForwarding(socketService);
          console.log('SmartChatService: Using Socket.IO connection');
          return;

        } catch (error) {
          console.warn('SmartChatService: Socket.IO connection failed, falling back to HTTP:', error.message);
        }
      }

      // Fallback to HTTP
      console.log('SmartChatService: Trying HTTP connection...');
      await httpChatService.connect();
      this.activeService = httpChatService;
      this.setupEventForwarding(httpChatService);
      console.log('SmartChatService: Using HTTP connection');

    } catch (error) {
      console.error('SmartChatService: Connection failed:', error);

      // Provide more specific error message
      if (error.message.includes('deployment protection')) {
        throw new Error('Backend deployment protection is enabled. Please disable it in Vercel dashboard to use the chatbot.');
      }

      throw new Error(`Failed to establish connection with chat service: ${error.message}`);
    } finally {
      this.isConnecting = false;
    }
  }

  // Setup event forwarding from the active service
  setupEventForwarding(service) {
    // Clear any existing listeners
    this.clearEventForwarding();

    // Forward all events from the active service
    const events = [
      'connect', 'disconnect', 'connect_error', 'reconnect',
      'reconnect_attempt', 'reconnect_failed', 'session_joined',
      'message', 'typing', 'error', 'pong'
    ];

    this.activeForwarders = [];

    events.forEach(event => {
      const handler = (data) => {
        this.emit(event, data);
      };
      service.on(event, handler);
      this.activeForwarders.push({ event, handler, service });
    });
  }

  // Clear event forwarding
  clearEventForwarding() {
    if (this.activeForwarders && Array.isArray(this.activeForwarders)) {
      this.activeForwarders.forEach(({ event, handler, service }) => {
        if (service && typeof service.off === 'function') {
          service.off(event, handler);
        }
      });
      this.activeForwarders = [];
    }
  }

  // Disconnect from active service
  disconnect() {
    this.clearEventForwarding();
    if (this.activeService) {
      this.activeService.disconnect();
      this.activeService = null;
    }
    this.listeners.clear();
  }

  // Join session
  async joinSession(sessionId = null) {
    if (!this.activeService) {
      throw new Error('No active connection');
    }
    return this.activeService.joinSession(sessionId);
  }

  // Send message
  async sendMessage(message, type = 'text', options = {}) {
    if (!this.activeService) {
      throw new Error('No active connection');
    }
    return this.activeService.sendMessage(message, type, options);
  }

  // Set typing indicator
  setTyping(isTyping) {
    if (this.activeService) {
      this.activeService.setTyping(isTyping);
    }
  }

  // Ping
  ping() {
    if (this.activeService) {
      return this.activeService.ping();
    }
    return false;
  }

  // Get connection status
  getConnectionStatus() {
    if (this.activeService) {
      const status = this.activeService.getConnectionStatus();
      return {
        ...status,
        activeService: this.activeService === socketService ? 'socket' : 'http'
      };
    }
    return {
      isConnected: false,
      activeService: null
    };
  }

  // Check if healthy
  isHealthy() {
    return this.activeService ? this.activeService.isHealthy() : false;
  }

  // Event listener management
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  // Get active service type
  getActiveServiceType() {
    if (!this.activeService) return null;
    return this.activeService === socketService ? 'socket' : 'http';
  }

  // Force switch to HTTP mode (useful for serverless environments)
  forceHttpMode() {
    console.log('SmartChatService: Forcing HTTP mode');
    this.preferredService = 'http';
    if (this.activeService === socketService) {
      this.disconnect();
    }
  }

  // Force switch to Socket mode
  forceSocketMode() {
    console.log('SmartChatService: Forcing Socket mode');
    this.preferredService = 'socket';
    if (this.activeService === httpChatService) {
      this.disconnect();
    }
  }
}

// Create singleton instance
const smartChatService = new SmartChatService();

// Auto-detect serverless environment and force HTTP mode
const isProduction = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENV === 'production';
const isServerlessBackend = process.env.REACT_APP_BACKEND_URL?.includes('vercel.app') ||
  process.env.REACT_APP_BACKEND_URL?.includes('netlify.app') ||
  process.env.REACT_APP_BACKEND_URL?.includes('onrender.com');

console.log('SmartChatService: Auto-detection:', {
  isProduction,
  isServerlessBackend,
  backendUrl: process.env.REACT_APP_BACKEND_URL,
  nodeEnv: process.env.NODE_ENV,
  reactEnv: process.env.REACT_APP_ENV
});

if (isProduction || isServerlessBackend) {
  console.log('SmartChatService: Detected serverless/production environment, forcing HTTP mode');
  smartChatService.forceHttpMode();
}

export default smartChatService;